import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { numberLocale } from '@/lib/language';
import { useEnforcement } from '@/hooks/useEnforcement';
import { useSubscription } from '@/hooks/useSubscription';
import { getCurrency } from '@/lib/checkout';
import {
  PLANS,
  defaultBillingCycle,
  exceedsLargestBand,
  formatPlanPrice,
  formatProductsPhrase,
  isCapTier,
  planDisplayName,
} from '@/lib/plans';
import { trackConversion } from '@/lib/analytics';
import { Button } from '@/components/ui/button';
import { UpgradeModal } from '@/components/enforcement/UpgradeModal';
import { X, ArrowUpRight } from 'lucide-react';

/** Ausblenden gilt fuer die Sitzung (UX-Audit A7O-15), nicht nur bis zum Reload. */
export const UPGRADE_BANNER_DISMISS_KEY = 'fo-upgrade-banner-dismissed';

function readDismissed(): boolean {
  try {
    return sessionStorage.getItem(UPGRADE_BANNER_DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

function writeDismissed(): void {
  try {
    sessionStorage.setItem(UPGRADE_BANNER_DISMISS_KEY, '1');
  } catch {
    // privater Modus: dann nur fuer diesen Mount
  }
}

interface UpgradeBannerProps {
  /**
   * Auf Fluid-Routen (`/workbench`) hat `<main>` kein Padding; der Banner
   * bringt dann seinen eigenen Aussenabstand mit (UX-Audit A7O-15). Gesetzt
   * von `Layout.tsx` ueber `isFluid`.
   */
  inset?: boolean;
}

/**
 * Kapazitaets-Banner (YELLOW/RED). UX-Audit A7O-08/K38: der Knopf oeffnet den
 * Kapazitaets-Dialog, der je Kontotyp Checkout, Bandwechsel, Kontakt oder den
 * Eigentuemer anbietet. Vorher kaufte ein Klick ohne Auswahl direkt
 * `cap100000_yearly`. Sichtbar auch ohne Empfehlung (Sondertarif, Mitglied),
 * dann ohne Preiszeile. Reicht selbst das groesste Band nicht (A7O-06), steht
 * statt der Upgrade-Zeile der Kontakt-Satz.
 */
export const UpgradeBanner: React.FC<UpgradeBannerProps> = ({ inset = false }) => {
  const { t, i18n } = useTranslation('enforcement');
  const {
    phase,
    nextPlan,
    recommendedPlan,
    totalProducts,
    limit,
    isMemberOfForeignMc,
    billedOwnerEmail,
    billedPlanIsSpecial,
  } = useEnforcement();
  const { subscription } = useSubscription();
  const [dismissed, setDismissed] = useState<boolean>(readDismissed);
  const [modalOpen, setModalOpen] = useState(false);

  const effectivePlan = recommendedPlan || nextPlan;
  const isVisible = (phase === 'YELLOW' || phase === 'RED') && !dismissed;

  // Funnel-Event (Bericht 8.3 Punkt 6): feuert pro Sichtbarwerden genau einmal.
  useEffect(() => {
    if (isVisible) trackConversion('upgrade_banner_shown', { phase });
  }, [isVisible, phase]);

  if (!isVisible) return null;

  const language = i18n?.language;
  const nf = (n: number) => n.toLocaleString(numberLocale(language));
  const isRed = phase === 'RED';
  const currency = getCurrency();
  const isBand = !!effectivePlan && isCapTier(effectivePlan.tier);
  // CRO 21.08.2026: Neuabschluesse sehen den Jahrespreis, Bestandskunden ihr Intervall.
  const cycle = isBand ? defaultBillingCycle(subscription) : 'monthly';
  const largestBand = PLANS[PLANS.length - 1];
  const beyondBands =
    !!effectivePlan && isBand && effectivePlan.tier === largestBand.tier && exceedsLargestBand(totalProducts);

  // A2-NEU-6: gleiche Rangfolge wie UpgradeModal und Handlungskarte. Ein
  // Sondertarif kauft nicht selbst, "brauchen ein Upgrade" waere dort falsch.
  const special = billedPlanIsSpecial && !isMemberOfForeignMc;

  const statusSentence = isRed
    ? isMemberOfForeignMc && billedOwnerEmail
      ? t('banner.limitReachedOwner', { email: billedOwnerEmail, limit: nf(limit) })
      : special
        ? t('banner.limitReachedSpecial', { limit: nf(limit) })
        : t('banner.limitReached', { limit: nf(limit) })
    : totalProducts > limit
      ? t('banner.overLimit', { total: nf(totalProducts), limit: nf(limit) })
      : t('banner.nearLimit');

  const onOpen = () => {
    trackConversion('upgrade_banner_clicked', { phase });
    setModalOpen(true);
  };

  const onDismiss = () => {
    writeDismissed();
    setDismissed(true);
  };

  const placement = inset ? 'mx-4 my-3 shrink-0 sm:mx-8 lg:mx-12' : 'mb-4';

  return (
    <>
      <div
        data-testid="upgrade-banner"
        className={`${
          isRed
            ? 'bg-destructive-light border border-destructive/30'
            : 'bg-warning-light border border-warning/30'
        } ${placement} rounded-lg px-4 py-3 print:hidden flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between`}
      >
        <p className={`min-w-0 flex-1 text-sm font-medium ${isRed ? 'text-destructive-ink' : 'text-warning-ink'}`}>
          {statusSentence}{' '}
          {beyondBands && t('modal.noBandCovers', { total: nf(totalProducts) })}
          {effectivePlan && !beyondBands && (
            <>
              {/* Der Doppelpunkt steht hier und nicht im Schluessel, damit er
                  nicht in den fetten Lauf gerät. */}
              <span className="font-semibold">
                {t('banner.upgradeTo', { plan: planDisplayName(effectivePlan, language, t) })}
              </span>
              {': '}
              {isBand
                ? t(cycle === 'yearly' ? 'banner.priceStartingYearly' : 'banner.priceStarting', {
                    price: formatPlanPrice(effectivePlan, currency, cycle, language),
                  })
                : t('banner.productsStarting', {
                    label: formatProductsPhrase(effectivePlan, language, t),
                    price: formatPlanPrice(effectivePlan, currency, 'monthly', language),
                  })}{' '}
              <Link
                to="/pricing"
                onClick={() => trackConversion('upgrade_banner_clicked', { phase })}
                className={isRed ? 'underline hover:text-destructive-ink/80' : 'underline hover:text-warning-ink/80'}
              >
                {t('banner.comparePlans')}
              </Link>
            </>
          )}
        </p>
        <div className="flex items-center gap-2 sm:flex-shrink-0">
          <Button onClick={onOpen} className="h-11 flex-1 gap-1 sm:h-9 sm:flex-none">
            <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
            {special ? t('banner.detailsSpecial') : t('banner.details')}
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-11 w-11 sm:h-7 sm:w-7"
            onClick={onDismiss}
            aria-label={t('banner.dismiss')}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <UpgradeModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
};
